import { HandHeart, Heart, Loader2, ShieldAlert, Users2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { adopt, listGlobalPool } from '@/api/member';
import Pagination from '@/components/member/Pagination';
import PIFAction from '@/components/member/PIFAction';

/**
 * GlobalPoolSubpage — dedicated member subpage for the Global PIF Pool.
 *
 * Lifts the "Adoption Portal" out of TransfersSubpage so it's discoverable
 * as a first-class destination from the sidebar. Behaviorally identical to
 * the prior module:
 *   - GET /api/global-pool?page=N  → paginated list (50/page, oldest first)
 *   - POST /api/adopt              → adopter pays, orphan receives position(s),
 *                                    upline rewires from "0" → adopter.
 *
 * Patch v18.0 — the SelfExchange widget referenced by the previous version
 * of this comment was removed along with the self-exchange capability. The
 * /api/me/swap endpoint now returns HTTP 410 Gone.
 */
const GlobalPoolSubpage = ({ onRefresh }) => {
  const [poolPage, setPoolPage] = useState(1);
  const [pool, setPool] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listGlobalPool(poolPage);
      setPool(data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Could not load pool: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [poolPage]);

  useEffect(() => {
    load();
  }, [load]);

  const handleAdopt = async (orphanId, item, fundSource) => {
    try {
      await adopt(orphanId, item, fundSource);
      toast.success("Orphan adopted — they're in your tree now.");
      await load();
      await onRefresh?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === 'object' ? detail?.message || detail?.code : detail || err.message;
      toast.error(`Adopt failed: ${msg}`);
    }
  };

  const total = pool?.total ?? 0;

  return (
    <div className="space-y-6" data-testid="subpage-global-pool">
      {/* Educational hero — explains the 7-day rule + how adoption works */}
      <section className="sc-glass-strong rounded-2xl p-5 md:p-7 relative overflow-hidden" style={{ border: '1px solid rgba(255, 182, 18, 0.32)' }}>
        <div
          className="absolute -top-20 -right-20 w-80 h-80 rounded-full pointer-events-none"
          style={{
            background: 'radial-gradient(circle, rgba(255,182,18,0.18), transparent 60%)',
          }}
          aria-hidden="true"
        />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center border shrink-0"
              style={{
                background: 'rgba(255,182,18,0.08)',
                borderColor: 'rgba(255,182,18,0.32)',
              }}
              aria-hidden="true"
            >
              <Heart className="w-5 h-5 text-[#FFC20E]" />
            </div>
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Global PIF Pool · Rescue &amp; Earn</div>
              <h1 className="font-display font-bold text-2xl md:text-3xl mt-0.5">
                Adopt an <span className="sc-gold-text">orphaned member</span>
              </h1>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mt-2">
            <div
              className="rounded-xl p-4 border"
              style={{
                background: 'rgba(0,0,0,0.32)',
                borderColor: 'rgba(255,182,18,0.24)',
              }}
            >
              <div className="text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)] flex items-center gap-1.5">
                <ShieldAlert className="w-3 h-3 text-[#FFC20E]" />
                Why members are here
              </div>
              <div className="text-[13px] text-[var(--sc-text-bright)] leading-snug mt-2">
                Every signup gets <strong>7 days</strong> for their sponsor to PIF them. If nothing happens, they enter this pool.
              </div>
            </div>
            <div
              className="rounded-xl p-4 border"
              style={{
                background: 'rgba(0,0,0,0.32)',
                borderColor: 'rgba(255,182,18,0.24)',
              }}
            >
              <div className="text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)] flex items-center gap-1.5">
                <HandHeart className="w-3 h-3 text-[#FFC20E]" />
                How adoption works
              </div>
              <div className="text-[13px] text-[var(--sc-text-bright)] leading-snug mt-2">
                Pick anyone below, buy a position (or subpack) <strong>for them</strong>, and they become your direct referral forever.
              </div>
            </div>
            <div
              className="rounded-xl p-4 border"
              style={{
                background: 'rgba(0,0,0,0.32)',
                borderColor: 'rgba(255,182,18,0.24)',
              }}
            >
              <div className="text-[10px] uppercase tracking-[0.20em] text-[var(--sc-text-mute)] flex items-center gap-1.5">
                <Users2 className="w-3 h-3 text-[#FFC20E]" />
                What you earn
              </div>
              <div className="text-[13px] text-[var(--sc-text-bright)] leading-snug mt-2">
                Every time their positions cycle, you get a matching bonus. Network compounding starts at zero risk.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The pool table */}
      <section className="sc-glass-strong rounded-2xl p-5 md:p-6" data-testid="global-pool-table-section">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <div>
            <div className="sc-pill">
              <span className="sc-dot" />
              Active orphans
            </div>
            <h3 className="font-display font-bold text-xl md:text-2xl mt-2">Orphaned members available for adoption</h3>
            <p className="text-[12px] text-[var(--sc-text-dim)] mt-1 max-w-2xl">
              Oldest first. Adopt to lock them into your downline before someone else does.
            </p>
          </div>
          <div
            className="flex items-center gap-2 text-[var(--sc-text-mute)] text-[11px] uppercase tracking-[0.22em] px-2.5 py-1.5 rounded-full border"
            style={{
              background: 'rgba(255,182,18,0.08)',
              borderColor: 'rgba(255,182,18,0.32)',
            }}
            data-testid="global-pool-count"
          >
            <Users2 className="w-3.5 h-3.5" />
            {total} {total === 1 ? 'orphan' : 'orphans'}
          </div>
        </div>

        {loading ? (
          <div className="p-8 flex items-center justify-center text-[var(--sc-text-dim)] text-sm" data-testid="global-pool-loading">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading…
          </div>
        ) : !pool?.items?.length ? (
          <div className="p-10 text-center text-[var(--sc-text-dim)] text-sm" data-testid="global-pool-empty">
            The global inactive pool is empty. Check back after the next hourly sweep.
          </div>
        ) : (
          <div className="overflow-x-auto -mx-2 md:mx-0">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] border-b border-[rgba(255,182,18,0.10)]">
                  <th className="text-left font-medium py-3 px-3">Username</th>
                  <th className="text-left font-medium py-3 px-3 hidden md:table-cell">Email</th>
                  <th className="text-left font-medium py-3 px-3 hidden lg:table-cell">Joined</th>
                  <th className="text-right font-medium py-3 px-3">Adopt via PIF</th>
                </tr>
              </thead>
              <tbody>
                {pool.items.map((row) => (
                  <tr
                    key={row.id}
                    className="border-b border-[rgba(255,182,18,0.05)] hover:bg-[rgba(255,182,18,0.025)] transition"
                    data-testid={`orphan-row-${row.username}`}
                  >
                    <td className="py-3 px-3">
                      <div className="font-semibold flex items-center gap-2">
                        <HandHeart className="w-3.5 h-3.5 text-[#FFC20E]" />
                        {row.username}
                      </div>
                    </td>
                    <td className="py-3 px-3 hidden md:table-cell text-[var(--sc-text-dim)]">{row.email}</td>
                    <td className="py-3 px-3 hidden lg:table-cell text-[var(--sc-text-mute)] text-[12px]">{String(row.created_at).slice(0, 10)}</td>
                    <td className="py-3 px-3 text-right">
                      <PIFAction label="Adopt via PIF" testid={`adopt-${row.username}`} onSubmit={(item, src) => handleAdopt(row.id, item, src)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {pool && pool.total_pages > 1 && <Pagination page={pool.page} totalPages={pool.total_pages} total={pool.total} onChange={setPoolPage} />}
      </section>
    </div>
  );
};

export default GlobalPoolSubpage;
